import { LoginForm } from '@/components/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  checkDeviceAuthorization,
  createDeviceAuthorizationRequest,
  updateDeviceLastUsed,
} from '@/services/deviceAuthorizationService';
import { supabase } from '@/services/supabaseClient';

const SAVED_EMAIL_KEY = 'luxori_saved_email';

/* ═══════════════════════════════════════════════════════════════════════
   ANIMATED BACKGROUND — SaaS premium dark theme
   Layer 1: CSS gradient (radial + linear)
   Layer 2: Glow halos (divs + blur + pulse animation)
   Layer 3: SVG mesh network (animated lines)
   Layer 4: SVG flowing waves
   ═══════════════════════════════════════════════════════════════════════ */
function AnimatedBackground() {
  const meshRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = meshRef.current;
    if (!svg) return;
    let animId: number;
    let t = 0;

    // ── Mesh nodes ──────────────────────────────────────────────────
    interface MeshNode {
      bx: number;
      by: number;
      x: number;
      y: number;
      ox: number;
      oy: number;
      speed: number;
    }
    const nodes: MeshNode[] = [];
    const COUNT = 50;
    const MAX_DIST = 220;

    for (let i = 0; i < COUNT; i++) {
      const bx = Math.random() * 1920;
      const by = Math.random() * 1080;
      nodes.push({
        bx,
        by,
        x: bx,
        y: by,
        ox: Math.random() * Math.PI * 2,
        oy: Math.random() * Math.PI * 2,
        speed: 0.2 + Math.random() * 0.4,
      });
    }

    // Pre-create SVG elements
    const lineGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const circleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    svg.appendChild(lineGroup);
    svg.appendChild(circleGroup);

    // Create circles
    const circles = nodes.map(() => {
      const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      c.setAttribute('r', (1 + Math.random() * 1.5).toFixed(1));
      c.setAttribute('fill', 'rgba(255, 255, 255, 0.35)');
      circleGroup.appendChild(c);
      return c;
    });

    // Create line pool
    const LINE_POOL = 300;
    const lines: SVGLineElement[] = [];
    for (let i = 0; i < LINE_POOL; i++) {
      const l = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      l.setAttribute('stroke', 'rgba(255, 255, 255, 0.1)');
      l.setAttribute('stroke-width', '0.5');
      l.style.display = 'none';
      lineGroup.appendChild(l);
      lines.push(l);
    }

    function tick() {
      t += 0.003;

      // Update node positions (gentle floating)
      for (const n of nodes) {
        n.x = n.bx + Math.sin(t * n.speed + n.ox) * 25;
        n.y = n.by + Math.cos(t * n.speed * 0.7 + n.oy) * 20;
      }

      // Update circles
      for (let i = 0; i < nodes.length; i++) {
        circles[i].setAttribute('cx', nodes[i].x.toFixed(1));
        circles[i].setAttribute('cy', nodes[i].y.toFixed(1));
      }

      // Update lines (connections)
      let li = 0;
      for (let i = 0; i < nodes.length && li < LINE_POOL; i++) {
        for (let j = i + 1; j < nodes.length && li < LINE_POOL; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const alpha = (1 - dist / MAX_DIST) * 0.18;
            const line = lines[li];
            line.setAttribute('x1', nodes[i].x.toFixed(1));
            line.setAttribute('y1', nodes[i].y.toFixed(1));
            line.setAttribute('x2', nodes[j].x.toFixed(1));
            line.setAttribute('y2', nodes[j].y.toFixed(1));
            line.setAttribute('stroke', `rgba(255, 255, 255, ${alpha.toFixed(3)})`);
            line.style.display = '';
            li++;
          }
        }
      }
      // Hide unused lines
      for (let k = li; k < LINE_POOL; k++) {
        lines[k].style.display = 'none';
      }

      animId = requestAnimationFrame(tick);
    }

    tick();
    return () => {
      cancelAnimationFrame(animId);
      svg.removeChild(lineGroup);
      svg.removeChild(circleGroup);
    };
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
      {/* Background: mauve-violet base + light blue-lavender halos */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at 100% 0%, rgba(160,180,210,0.9) 0%, rgba(160,180,210,0.9) 5%, rgba(160,180,210,0.6) 15%, rgba(160,180,210,0.25) 25%, rgba(160,180,210,0) 45%),
            radial-gradient(circle at 0% 100%, rgba(150,160,200,0.9) 0%, rgba(150,160,200,0.9) 5%, rgba(150,160,200,0.5) 15%, rgba(150,160,200,0.18) 25%, rgba(150,160,200,0) 45%),
            #4a4570
          `,
        }}
      />

      {/* Layer 3 — SVG mesh network */}
      <svg
        ref={meshRef}
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
      />

      {/* Layer 4 — Dense flowing curves + straight lines (SVG) */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* ── Top-right area: dense curved bundle ── */}
        <path
          d="M1920,0 Q1600,150 1400,50 T1100,200 Q900,300 700,180"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1"
          style={{ animation: 'waveFloat1 14s ease-in-out infinite' }}
        />
        <path
          d="M1920,30 Q1580,180 1380,80 T1080,230 Q880,320 680,210"
          fill="none"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat2 15s ease-in-out 0.5s infinite' }}
        />
        <path
          d="M1920,60 Q1560,200 1360,100 T1060,260 Q860,340 660,240"
          fill="none"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 16s ease-in-out 1s infinite' }}
        />
        <path
          d="M1920,90 Q1540,220 1340,120 T1040,290 Q840,370 640,260"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 17s ease-in-out 1.5s infinite' }}
        />
        <path
          d="M1920,120 Q1520,250 1320,140 T1020,320 Q820,400 620,290"
          fill="none"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 18s ease-in-out 2s infinite' }}
        />

        {/* ── Right-side flowing curves ── */}
        <path
          d="M1200,100 Q1400,250 1300,450 T1500,800 Q1600,950 1920,900"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1.2"
          style={{ animation: 'waveFloat1 15s ease-in-out infinite' }}
        />
        <path
          d="M1230,130 Q1430,280 1330,480 T1530,830 Q1630,970 1920,930"
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="1"
          style={{ animation: 'waveFloat2 16s ease-in-out 0.5s infinite' }}
        />
        <path
          d="M1260,160 Q1460,310 1360,510 T1560,860 Q1660,990 1920,960"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 17s ease-in-out 1s infinite' }}
        />
        <path
          d="M1100,50 Q1350,200 1250,400 T1550,750 Q1700,900 1920,850"
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="1"
          style={{ animation: 'waveFloat2 18s ease-in-out 1s infinite' }}
        />
        <path
          d="M1130,80 Q1380,230 1280,430 T1580,780 Q1730,930 1920,880"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 19s ease-in-out 1.5s infinite' }}
        />
        <path
          d="M1300,150 Q1500,300 1400,500 T1600,850 Q1750,1000 1920,950"
          fill="none"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="1"
          style={{ animation: 'waveFloat1 20s ease-in-out 3s infinite' }}
        />
        <path
          d="M1330,180 Q1530,330 1430,530 T1630,880 Q1780,1020 1920,980"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat2 21s ease-in-out 3.5s infinite' }}
        />
        <path
          d="M1250,0 Q1450,180 1350,380 T1580,720 Q1680,880 1920,820"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="1"
          style={{ animation: 'waveFloat2 16s ease-in-out 0.5s infinite' }}
        />
        <path
          d="M1350,200 Q1550,350 1420,550 T1650,900 Q1800,1050 1920,1000"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 22s ease-in-out 2s infinite' }}
        />

        {/* ── Bottom-left area: dense curved bundle ── */}
        <path
          d="M0,1080 Q320,930 520,1030 T820,880 Q1020,780 1220,900"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1"
          style={{ animation: 'waveFloat2 14s ease-in-out infinite' }}
        />
        <path
          d="M0,1050 Q300,900 500,1000 T800,850 Q1000,750 1200,870"
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 15s ease-in-out 0.5s infinite' }}
        />
        <path
          d="M0,1020 Q280,870 480,970 T780,820 Q980,720 1180,840"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat2 16s ease-in-out 1s infinite' }}
        />
        <path
          d="M0,990 Q260,840 460,940 T760,790 Q960,690 1160,810"
          fill="none"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 17s ease-in-out 1.5s infinite' }}
        />
        <path
          d="M0,960 Q240,810 440,910 T740,760 Q940,660 1140,780"
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 18s ease-in-out 2s infinite' }}
        />

        {/* ── Left-side flowing curves ── */}
        <path
          d="M0,600 Q200,500 400,650 T800,550 Q1000,500 1200,620"
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="1"
          style={{ animation: 'waveFloat2 22s ease-in-out 2s infinite' }}
        />
        <path
          d="M0,630 Q220,530 420,680 T820,580 Q1020,520 1220,640"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 23s ease-in-out 2.5s infinite' }}
        />
        <path
          d="M0,700 Q250,600 500,720 T900,620 Q1100,560 1300,680"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat1 25s ease-in-out 1s infinite' }}
        />
        <path
          d="M0,730 Q270,630 520,750 T920,650 Q1120,590 1320,710"
          fill="none"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 26s ease-in-out 1.5s infinite' }}
        />
        <path
          d="M0,500 Q180,420 380,560 T750,480 Q950,430 1150,540"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.8"
          style={{ animation: 'waveFloat2 20s ease-in-out 3s infinite' }}
        />
        <path
          d="M0,530 Q200,450 400,590 T770,510 Q970,460 1170,570"
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 21s ease-in-out 3.5s infinite' }}
        />
        <path
          d="M0,800 Q300,720 550,830 T950,730 Q1150,670 1400,760"
          fill="none"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 24s ease-in-out 5s infinite' }}
        />
        <path
          d="M0,400 Q150,320 350,440 T700,380 Q880,320 1050,430"
          fill="none"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 19s ease-in-out 4s infinite' }}
        />
        <path
          d="M0,300 Q120,230 300,350 T650,280 Q830,220 1000,330"
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat1 21s ease-in-out 2s infinite' }}
        />

        {/* ── Straight diagonal lines (dense) ── */}
        {/* From left edge going up-right */}
        <line
          x1="0"
          y1="100"
          x2="500"
          y2="0"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 18s ease-in-out infinite' }}
        />
        <line
          x1="0"
          y1="250"
          x2="750"
          y2="0"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 20s ease-in-out 1s infinite' }}
        />
        <line
          x1="0"
          y1="400"
          x2="1000"
          y2="0"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 22s ease-in-out 2s infinite' }}
        />
        <line
          x1="0"
          y1="550"
          x2="1250"
          y2="0"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat2 19s ease-in-out 0.5s infinite' }}
        />
        <line
          x1="0"
          y1="700"
          x2="1500"
          y2="0"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat1 21s ease-in-out 1.5s infinite' }}
        />
        <line
          x1="0"
          y1="850"
          x2="1750"
          y2="0"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat2 23s ease-in-out 3s infinite' }}
        />
        <line
          x1="0"
          y1="1000"
          x2="1920"
          y2="100"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat1 25s ease-in-out 4s infinite' }}
        />
        {/* From right edge going down-left */}
        <line
          x1="1920"
          y1="100"
          x2="1400"
          y2="1080"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 17s ease-in-out infinite' }}
        />
        <line
          x1="1920"
          y1="250"
          x2="1200"
          y2="1080"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat1 19s ease-in-out 0.5s infinite' }}
        />
        <line
          x1="1920"
          y1="400"
          x2="1000"
          y2="1080"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="0.7"
          style={{ animation: 'waveFloat2 21s ease-in-out 1s infinite' }}
        />
        <line
          x1="1920"
          y1="550"
          x2="800"
          y2="1080"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat1 23s ease-in-out 2s infinite' }}
        />
        <line
          x1="1920"
          y1="700"
          x2="600"
          y2="1080"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat2 24s ease-in-out 2.5s infinite' }}
        />
        <line
          x1="1920"
          y1="850"
          x2="400"
          y2="1080"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat1 26s ease-in-out 3.5s infinite' }}
        />
        <line
          x1="1920"
          y1="1000"
          x2="200"
          y2="1080"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat2 27s ease-in-out 4.5s infinite' }}
        />
        {/* Cross diagonals */}
        <line
          x1="200"
          y1="1080"
          x2="900"
          y2="0"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat1 24s ease-in-out 3s infinite' }}
        />
        <line
          x1="500"
          y1="1080"
          x2="1200"
          y2="0"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="0.6"
          style={{ animation: 'waveFloat2 26s ease-in-out 4s infinite' }}
        />
        <line
          x1="800"
          y1="1080"
          x2="1500"
          y2="0"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat1 25s ease-in-out 3.5s infinite' }}
        />
        <line
          x1="1100"
          y1="1080"
          x2="1800"
          y2="0"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat2 28s ease-in-out 5s infinite' }}
        />
        <line
          x1="1700"
          y1="0"
          x2="800"
          y2="1080"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat1 25s ease-in-out 3.5s infinite' }}
        />
        <line
          x1="1400"
          y1="0"
          x2="500"
          y2="1080"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.5"
          style={{ animation: 'waveFloat2 27s ease-in-out 5s infinite' }}
        />
      </svg>

      {/* CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes haloPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.08); }
        }
        @keyframes waveFloat1 {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-15px) translateX(10px); }
          50% { transform: translateY(10px) translateX(-8px); }
          75% { transform: translateY(-8px) translateX(12px); }
        }
        @keyframes waveFloat2 {
          0%, 100% { transform: translateY(0) translateX(0); }
          33% { transform: translateY(12px) translateX(-10px); }
          66% { transform: translateY(-10px) translateX(8px); }
        }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   LOGIN PAGE
   ═══════════════════════════════════════════════════════════════════════ */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [devicePending, setDevicePending] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      setTimeout(() => passwordInputRef.current?.focus(), 100);
    } else {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
        setError('Connexion trop lente, veuillez réessayer');
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (!email.includes('@')) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login(email, password);

      if (rememberMe) {
        localStorage.setItem(SAVED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(SAVED_EMAIL_KEY);
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('Session utilisateur non trouvée');
      }

      const userId = session.user.id;

      const { data: userProfile } = await supabase
        .from('users')
        .select('roles(nom)')
        .eq('auth_user_id', userId)
        .single();

      const userRole = (userProfile?.roles as { nom?: string } | null)?.nom;

      if (userRole !== 'admin' && userRole !== 'Super Administrateur') {
        const { authorized, fingerprint } = await checkDeviceAuthorization(userId);

        if (!authorized) {
          await createDeviceAuthorizationRequest(userId);
          await supabase.auth.signOut();
          setDevicePending(true);
          setError('');
          setIsLoading(false);
          return;
        }

        await updateDeviceLastUsed(userId, fingerprint);
      }

      // Rediriger vers la page d'origine si disponible (ex: lien email device-auth)
      const from = (location.state as { from?: { pathname: string; search: string } })?.from;
      const redirectTo = from ? `${from.pathname}${from.search || ''}` : '/';
      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      let errorMessage = 'Une erreur est survenue lors de la connexion';

      if (message.includes('Invalid login credentials')) {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (message.includes('Email not confirmed')) {
        errorMessage = 'Veuillez confirmer votre email avant de vous connecter';
      } else if (message.includes('Too many requests')) {
        errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
      } else if (message.includes('User not found')) {
        errorMessage = 'Aucun compte trouvé avec cette adresse email';
      } else if (message) {
        errorMessage = message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-dvh flex items-center justify-center px-4 relative animate-[fadeIn_0.8s_ease-out]">
      {/* Background */}
      <AnimatedBackground />

      {/* Content */}
      <div className="relative z-10 w-full max-w-md animate-[slideUp_0.6s_ease-out_0.2s_both]">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img
              src="https://pjcgtowjtvfzpedkgedm.supabase.co/storage/v1/object/public/logos/logo_Luxori.png"
              alt="Luxori"
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-light text-white/90">
                <span className="font-semibold">Luxori</span>
              </h1>
              <p className="text-sm text-white/50">Gestion commerciale</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-white/30" aria-hidden>
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Card form */}
        <div className="rounded-xl bg-white p-8 shadow-xl border border-gray-200 transition-colors duration-200">
          <LoginForm
            email={email}
            password={password}
            rememberMe={rememberMe}
            onEmailChange={v => {
              setEmail(v);
              if (error) setError('');
            }}
            onPasswordChange={v => {
              setPassword(v);
              if (error) setError('');
            }}
            onRememberMeChange={setRememberMe}
            onSubmit={handleSubmit}
            emailInputRef={emailInputRef}
            passwordInputRef={passwordInputRef}
            isLoading={isLoading}
            error={error}
            devicePending={devicePending}
          />
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-white/40">
          &copy; {new Date().getFullYear()} GMC Information Technology. Tous droits
          r&eacute;serv&eacute;s.
        </p>
      </div>
    </div>
  );
}
